import { useCallback, useMemo } from 'react'
import { Line3, Mesh, Plane, Shape, Vector2, Vector3 } from 'three'
import { POSITIVE_Z } from '~/components/DrawingCanvas/constants/dimensions'
import { useRoofMeshes } from '~/components/DrawingCanvas/Roof/hooks/useRoofMeshes'
import { modelToUI, vectorModelToUI, vectorUIToModel } from '~/components/DrawingCanvas/util/units'
import { DEFAULT_WALL_SIZES } from '~/config/wallSizes'
import { useAppSelector } from '~/store/hooks'
import { GridLine, RoofSection } from '~/store/objects/types'
import { Edge, isPointInPolygon } from '~/utils/pointInPolygon'

const VERTICAL_THRESHOLD = 0.0001
const SEGMENT_LENGTH_THRESHOLD = 1

export const PrimaryStructure = () => {
  const isVisible = useAppSelector(state => state.layers.layers.PRIMARY_MOUNTING_STRUCTURE.visible)
  const roofMeshes = useRoofMeshes()
  const roofSections = Object.entries(useAppSelector(state => state.objects.present.roofSections))

  return (
    <group visible={isVisible}>
      {roofSections.map(([id, section]) => {
        if (!section.beamModels || !section.primaryStructuresEnabled) return <></>
        return (
          <group key={id}>
            {section.beamModels.map(model => {
              return <Beam key={model.id} beamModel={model} roofMeshes={roofMeshes} roofSection={section} />
            })}
          </group>
        )
      })}
    </group>
  )
}

type BeamProps = {
  roofMeshes: ReturnType<typeof useRoofMeshes>
  roofSection: RoofSection
  beamModel: GridLine
}

const Beam = ({ beamModel, roofMeshes, roofSection }: BeamProps) => {
  const roofs = useAppSelector(state => state.objects.present.roofs)
  const roofHeight = roofs[roofSection.roofId]?.height ?? DEFAULT_WALL_SIZES.EXTERIOR_WALLS.thickness
  const isVertical = roofSection.beamRotation === 0

  const intersectionPoints = useMemo(() => {
    const plane = new Plane()
    const { x, y, z } = beamModel.position
    const normal = isVertical ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0)
    plane.setFromNormalAndCoplanarPoint(normal, new Vector3(x, y, z))
    const intersectionPoints: Vector3[] = []
    for (const [, mesh] of roofMeshes) {
      const index = mesh.geometry.index!
      const position = mesh.geometry.attributes.position
      const faceCount = index.count / 3
      const a = new Vector3()
      const b = new Vector3()
      const c = new Vector3()
      for (let i = 0; i < faceCount; i++) {
        const baseIndex = i * 3
        a.fromBufferAttribute(position, index.getX(baseIndex))
        b.fromBufferAttribute(position, index.getX(baseIndex + 1))
        c.fromBufferAttribute(position, index.getX(baseIndex + 2))
        const lineAB = new Line3(a, b)
        const lineBC = new Line3(b, c)
        const lineCA = new Line3(c, a)
        getPlaneIntersections(lineAB, plane, intersectionPoints)
        getPlaneIntersections(lineBC, plane, intersectionPoints)
        getPlaneIntersections(lineCA, plane, intersectionPoints)
      }
    }
    if (roofSection.structureStyle === 'flat') {
      intersectionPoints.forEach(point => point.setZ(roofHeight))
    }
    intersectionPoints.sort((a, b) => (isVertical ? a.y - b.y : a.x - b.x))
    return intersectionPoints
  }, [beamModel, roofMeshes, roofSection, roofHeight, isVertical])

  return (
    <group>
      {intersectionPoints.map((point, i, points) => {
        if (i === 0) return <></>
        const start = vectorModelToUI(points[i - 1])
        const end = vectorModelToUI(point)
        const segment = new Vector3().subVectors(start, end)
        const normalizedSegment = segment.clone().setZ(Math.abs(segment.z)).normalize()
        const isVerticalSegment = 1 - POSITIVE_Z.dot(normalizedSegment) < VERTICAL_THRESHOLD
        const isShortSegment = segment.length() < SEGMENT_LENGTH_THRESHOLD
        const midpoint = vectorUIToModel(start.clone().lerp(end, 0.5))
        const edges = roofSection.perimeterPoints.reduce<Edge[]>((acc, currPoint, i, arr) => {
          if (i === 0) return acc
          const prevPoint = arr[i - 1]
          acc.push({ p1: [currPoint[0], currPoint[1]], p2: [prevPoint[0], prevPoint[1]] })
          return acc
        }, [])
        const isInFacility = isPointInPolygon([midpoint.x, midpoint.y], edges)
        if (isVerticalSegment || isShortSegment || !isInFacility) return <></>
        const width = modelToUI(roofSection.beamWidth)
        const depth = modelToUI(roofSection.beamDepth)
        return <BeamSegment key={i} {...{ start, end, width, depth, isVertical }} />
      })}
    </group>
  )
}

const getPlaneIntersections = (line: Line3, plane: Plane, output: Vector3[]) => {
  const intersection = plane.intersectLine(line, new Vector3())
  if (intersection) output.push(intersection)
}

const square = new Shape([
  new Vector2(-0.5, 0.5),
  new Vector2(0.5, 0.5),
  new Vector2(0.5, -0.5),
  new Vector2(-0.5, -0.5),
])

const BeamSegment = (props: { start: Vector3; end: Vector3; width: number; depth: number; isVertical: boolean }) => {
  const { start, end, depth, isVertical, width } = props
  const distance = start.distanceTo(end)
  const ref = useCallback(
    (node: Mesh) => {
      if (!node) return
      node.position.copy(start)
      node.lookAt(end)
      node.scale.setX(depth)
      node.scale.setY(width)
      node.rotation.z = isVertical ? Math.PI / 2 : Math.PI
      node.position.z -= depth / 2
    },
    [start, end, depth, width, isVertical]
  )

  return (
    <mesh ref={ref}>
      <extrudeGeometry args={[square, { depth: distance }]} />
      <meshStandardMaterial color={0x888888} roughness={0.5} metalness={0.5} />
    </mesh>
  )
}
